import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Progress } from '../ui/progress';
import {
  ShieldCheck, ShieldAlert, ShieldX, RefreshCw, ChevronDown, ChevronRight, Search, Loader2,
} from 'lucide-react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_META = {
  permission: { label: 'Durci', color: 'bg-emerald-100 text-emerald-800 border-emerald-200', dot: 'bg-emerald-500' },
  permOrRole: { label: 'Soft', color: 'bg-amber-100 text-amber-800 border-amber-200', dot: 'bg-amber-500' },
  role: { label: 'Rôle en dur', color: 'bg-orange-100 text-orange-800 border-orange-200', dot: 'bg-orange-500' },
  scoping: { label: 'Data-scoping', color: 'bg-blue-100 text-blue-800 border-blue-200', dot: 'bg-blue-500' },
  public: { label: 'Public', color: 'bg-teal-100 text-teal-800 border-teal-200', dot: 'bg-teal-500' },
  self: { label: 'Self / ownership', color: 'bg-violet-100 text-violet-800 border-violet-200', dot: 'bg-violet-500' },
  none: { label: 'À durcir', color: 'bg-red-100 text-red-800 border-red-200', dot: 'bg-red-500' },
};

const Kpi = ({ icon: Icon, label, value, sub, color, testid }) => (
  <Card data-testid={testid}>
    <CardContent className="pt-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className={`text-3xl font-bold ${color}`}>{value}</p>
          {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
        </div>
        <Icon className={`w-7 h-7 ${color} opacity-80`} />
      </div>
    </CardContent>
  </Card>
);

const RbacCoverageAudit = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | role | none | permOrRole
  const [expanded, setExpanded] = useState({});

  const fetchCoverage = async (isRefresh = false) => {
    isRefresh ? setRefreshing(true) : setLoading(true);
    try {
      const headers = { Authorization: 'Bearer cookie-auth' };
      const res = await axios.get(`${API}/admin/rbac-audit/endpoint-coverage`, { headers });
      setData(res.data);
    } catch (e) {
      toast({
        title: 'Erreur',
        description: e?.response?.status === 403
          ? "Accès refusé : permission 'rbac.audit.view' requise."
          : "Impossible de charger l'audit de couverture RBAC.",
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchCoverage(); }, []);

  const endpointsByFile = useMemo(() => {
    const map = {};
    [
      ...(data?.nonHardened || []),
      ...(data?.scopingEndpoints || []),
      ...(data?.publicEndpoints || []),
      ...(data?.selfEndpoints || []),
    ].forEach((e) => {
      (map[e.file] = map[e.file] || []).push(e);
    });
    return map;
  }, [data]);

  const files = useMemo(() => {
    let list = data?.files || [];
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter((f) => f.file.toLowerCase().includes(q));
    }
    if (statusFilter !== 'all') {
      list = list.filter((f) => (f[statusFilter] || 0) > 0);
    }
    return list;
  }, [data, query, statusFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48" data-testid="rbac-audit-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#1db584]" />
      </div>
    );
  }

  if (!data) return null;

  const s = data.summary;

  return (
    <div className="space-y-6" data-testid="rbac-coverage-audit">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg md:text-xl font-bold text-gray-900">Audit de couverture RBAC</h2>
          <p className="text-sm text-gray-500">
            Progression du durcissement permission-gating des {s.total} endpoints backend.
          </p>
        </div>
        <Button
          onClick={() => fetchCoverage(true)}
          disabled={refreshing}
          variant="outline"
          className="gap-2"
          data-testid="rbac-audit-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Rafraîchir
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Kpi testid="rbac-kpi-hardened" icon={ShieldCheck} label="Durcis (permission)"
          value={`${s.pctHardened}%`} sub={`${s.permission} / ${s.total} endpoints`} color="text-emerald-600" />
        <Kpi testid="rbac-kpi-toharden" icon={ShieldX} label="Reste à traiter"
          value={s.toHarden ?? (s.role + s.none + s.permOrRole)} sub="role + none + soft" color="text-red-600" />
        <Kpi testid="rbac-kpi-scoping" icon={ShieldCheck} label="Data-scoping"
          value={s.scoping} sub="visibilité intentionnelle" color="text-blue-600" />
        <Kpi testid="rbac-kpi-public" icon={ShieldCheck} label="Public (par design)"
          value={s.public ?? 0} sub="auth-less intentionnel" color="text-teal-600" />
        <Kpi testid="rbac-kpi-self" icon={ShieldCheck} label="Self / ownership"
          value={s.self ?? 0} sub="mes données" color="text-violet-600" />
      </div>

      {/* Overall progress */}
      <Card data-testid="rbac-overall-progress">
        <CardContent className="pt-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Couverture globale</span>
            <span className="text-sm font-semibold text-emerald-600">{s.pctHardened}%</span>
          </div>
          <Progress value={s.pctHardened} className="h-3" />
          <div className="flex flex-wrap gap-3 mt-3 text-xs text-gray-500">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> {s.permission} durcis</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-orange-500" /> {s.role} rôle</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-blue-500" /> {s.scoping} data-scoping</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-teal-500" /> {s.public ?? 0} public</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-violet-500" /> {s.self ?? 0} self</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> {s.none} à durcir</span>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Rechercher un fichier de routes…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
            data-testid="rbac-audit-search"
          />
        </div>
        <div className="flex gap-2">
          {[
            { k: 'all', label: 'Tous' },
            { k: 'none', label: 'À durcir' },
            { k: 'role', label: 'Rôle en dur' },
            { k: 'scoping', label: 'Data-scoping' },
            { k: 'public', label: 'Public' },
            { k: 'self', label: 'Self' },
          ].map((f) => (
            <Button
              key={f.k}
              size="sm"
              variant={statusFilter === f.k ? 'default' : 'outline'}
              onClick={() => setStatusFilter(f.k)}
              data-testid={`rbac-filter-${f.k}`}
            >
              {f.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Per-file table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Par fichier de routes ({files.length})</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {files.map((f) => {
            const isOpen = !!expanded[f.file];
            const eps = endpointsByFile[f.file] || [];
            return (
              <div key={f.file} className="border rounded-lg" data-testid={`rbac-file-row-${f.file}`}>
                <button
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50 transition-colors"
                  onClick={() => setExpanded((p) => ({ ...p, [f.file]: !p[f.file] }))}
                  disabled={eps.length === 0}
                  data-testid={`rbac-file-toggle-${f.file}`}
                >
                  {eps.length > 0
                    ? (isOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />)
                    : <span className="w-4" />}
                  <span className="font-mono text-sm text-gray-800 flex-1 truncate">{f.file}</span>
                  <div className="hidden sm:flex items-center gap-1.5">
                    {f.permission > 0 && <Badge className={STATUS_META.permission.color}>{f.permission}</Badge>}
                    {f.role > 0 && <Badge className={STATUS_META.role.color}>{f.role}</Badge>}
                    {f.scoping > 0 && <Badge className={STATUS_META.scoping.color}>{f.scoping}</Badge>}
                    {f.public > 0 && <Badge className={STATUS_META.public.color}>{f.public}</Badge>}
                    {f.self > 0 && <Badge className={STATUS_META.self.color}>{f.self}</Badge>}
                    {f.none > 0 && <Badge className={STATUS_META.none.color}>{f.none}</Badge>}
                  </div>
                  <div className="w-28 flex items-center gap-2">
                    <Progress value={f.pctHardened} className="h-2 flex-1" />
                    <span className="text-xs text-gray-500 w-9 text-right">{f.pctHardened}%</span>
                  </div>
                </button>
                {isOpen && eps.length > 0 && (
                  <div className="border-t bg-gray-50/60 px-3 py-2 space-y-1" data-testid={`rbac-file-detail-${f.file}`}>
                    {eps.map((e, i) => {
                      const meta = STATUS_META[e.status];
                      return (
                        <div key={i} className="flex items-center gap-2 text-xs py-1">
                          <span className={`w-2 h-2 rounded-full ${meta.dot}`} />
                          <span className="font-semibold text-gray-600 w-14">{e.method}</span>
                          <span className="font-mono text-gray-700 flex-1 truncate">{e.path || '/'}</span>
                          <span className="text-gray-400">L{e.line}</span>
                          <Badge className={`${meta.color} text-[10px]`}>{meta.label}</Badge>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          {files.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-6">Aucun fichier ne correspond au filtre.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RbacCoverageAudit;
